import React, { useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";

import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DescriptionIcon from "@material-ui/icons/Description";
import VisibilityIcon from "@material-ui/icons/Visibility";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";

import { useBroadcastTemplatesPage } from "./hooks/useBroadcastTemplatesPage";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  searchRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    padding: theme.spacing(1, 2),
    flexWrap: "wrap",
  },
  searchField: { flex: 1, minWidth: 200 },
  categorySelect: { minWidth: 180 },
  chip: { fontSize: "0.75rem" },
  previewBox: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    background: theme.palette.grey[100],
    borderRadius: 4,
    fontSize: "0.875rem",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  charCount: {
    textAlign: "right",
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
}));

const CATEGORY_LABELS = {
  promotional:   "Promocional",
  informational: "Informativo",
  retention:     "Retención",
  transactional: "Transaccional",
};

const CATEGORY_COLORS = {
  promotional:   "primary",
  informational: "default",
  retention:     "secondary",
  transactional: "default",
};

export default function BroadcastTemplates() {
  const classes = useStyles();

  const {
    templates, loading, searchParam, categoryFilter,
    modalOpen, deleteModalOpen, selectedTemplate, form, saving, spintaxPreview,
    CATEGORIES,
    fetchTemplates, openCreateModal, openEditModal, closeModal,
    openDeleteModal, closeDeleteModal, handleFormChange,
    handleSpintaxPreview, handleSave, handleDelete,
    handleToggleActive, handleSearch, handleCategoryFilter,
  } = useBroadcastTemplatesPage();

  useEffect(() => {
    fetchTemplates(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          <DescriptionIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
          Plantillas de Difusión
        </Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreateModal}>
            Nueva Plantilla
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {/* Barra de búsqueda y filtros */}
        <div className={classes.searchRow}>
          <TextField
            className={classes.searchField}
            placeholder="Buscar por nombre..."
            value={searchParam}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon /></InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
          />
          <FormControl variant="outlined" size="small" className={classes.categorySelect}>
            <InputLabel>Categoría</InputLabel>
            <Select
              label="Categoría"
              value={categoryFilter}
              onChange={(e) => handleCategoryFilter(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* Tabla */}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Categoría</TableCell>
              <TableCell>Vista previa</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={6} />
            ) : templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary">No hay plantillas. Crea la primera.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              templates.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{t.id}</TableCell>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={CATEGORY_LABELS[t.category] || t.category}
                      color={CATEGORY_COLORS[t.category] || "default"}
                      size="small"
                      className={classes.chip}
                    />
                  </TableCell>
                  <TableCell style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {t.body}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={t.isActive ? "Desactivar" : "Activar"}>
                      <Switch
                        size="small"
                        color="primary"
                        checked={!!t.isActive}
                        onChange={() => handleToggleActive(t)}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => openEditModal(t)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => openDeleteModal(t)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Modal Crear / Editar */}
      <Dialog open={modalOpen} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedTemplate ? "Editar Plantilla" : "Nueva Plantilla de Difusión"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nombre *"
            fullWidth
            variant="outlined"
            size="small"
            value={form.name}
            onChange={(e) => handleFormChange("name", e.target.value)}
            style={{ marginBottom: 16 }}
            inputProps={{ maxLength: 100 }}
          />

          <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 16 }}>
            <InputLabel>Categoría *</InputLabel>
            <Select
              label="Categoría *"
              value={form.category}
              onChange={(e) => handleFormChange("category", e.target.value)}
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Mensaje *"
            fullWidth
            multiline
            rows={5}
            variant="outlined"
            size="small"
            value={form.body}
            onChange={(e) => handleFormChange("body", e.target.value)}
            inputProps={{ maxLength: 4096 }}
            helperText="Spintax: {variante1|variante2|variante3} para rotación anti-spam"
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
            <div className={classes.charCount}>{form.body.length}/4096</div>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  color="primary"
                  checked={!!form.isActive}
                  onChange={(e) => handleFormChange("isActive", e.target.checked)}
                />
              }
              label={
                <Typography variant="caption" style={{ color: form.isActive ? "#388e3c" : "#9e9e9e" }}>
                  {form.isActive ? "Activa" : "Inactiva"}
                </Typography>
              }
            />
          </div>

          {form.body.includes("{") && (
            <div style={{ marginTop: 8 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={handleSpintaxPreview}
              >
                Vista previa Spintax
              </Button>
              {spintaxPreview && (
                <div className={classes.previewBox}>
                  <strong>Resultado:</strong> {spintaxPreview.resolved}
                  {spintaxPreview.variants.length > 1 && (
                    <>
                      <br /><br />
                      <strong>Variantes del 1er grupo:</strong>
                      {spintaxPreview.variants.map((v, i) => (
                        <div key={i}>• {v}</div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={saving}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving || !form.name.trim() || !form.body.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? "Guardando..." : "Guardar Plantilla"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Confirmación Eliminar */}
      <ConfirmationModal
        title={`Eliminar plantilla "${selectedTemplate?.name}"?`}
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
      >
        Esta acción no se puede deshacer.
      </ConfirmationModal>
    </MainContainer>
  );
}
